const SCROLLABLE_OVERFLOW = /(auto|scroll|overlay)/;

/** Через границу Shadow DOM — к host: острова и модалки живут под shadow. */
const getFlatParent = (node: Element): Element | null => {
	if (node.parentElement) {
		return node.parentElement;
	}
	const root = node.getRootNode();
	return root instanceof ShadowRoot ? root.host : null;
};

/** null — прокручивается окно. */
export const getScrollParent = (node: Element): Element | null => {
	let current = getFlatParent(node);
	while (current && current !== document.body && current !== document.documentElement) {
		const { overflowY } = window.getComputedStyle(current);
		if (SCROLLABLE_OVERFLOW.test(overflowY) && current.scrollHeight > current.clientHeight) {
			return current;
		}
		current = getFlatParent(current);
	}
	return null;
};

/** На скрытой вкладке кадры не рисуются — smooth-прокрутка там не сдвинется. */
const shouldScrollInstantly = (): boolean =>
	document.visibilityState === 'hidden' ||
	(typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

interface Options {
	/** Сколько видимой высоты должно оставаться под блоком, чтобы не скроллить (0..1). */
	minVisibleRatio?: number;
}

/**
 * Скроллит, только если начало блока плохо видно: верх ушёл выше видимой области
 * (с учётом scroll-margin-top) или под блоком видно меньше minVisibleRatio высоты.
 * @returns true, если прокрутка запущена
 */
export const scrollToStartIfNeeded = (element: HTMLElement, { minVisibleRatio = 0.25 }: Options = {}): boolean => {
	if (typeof element.scrollIntoView !== 'function') {
		return false;
	}

	const container = getScrollParent(element);
	const viewport = container
		? container.getBoundingClientRect()
		: { top: 0, bottom: window.innerHeight };
	const offset = parseFloat(window.getComputedStyle(element).scrollMarginTop) || 0;
	const visibleTop = viewport.top + offset;
	const visibleHeight = viewport.bottom - visibleTop;
	const rect = element.getBoundingClientRect();

	const startHidden = rect.top < visibleTop - 1;
	const startAtBottom = rect.bottom > viewport.bottom - visibleHeight * minVisibleRatio;
	if (!startHidden && !startAtBottom) {
		return false;
	}

	element.scrollIntoView({ block: 'start', behavior: shouldScrollInstantly() ? 'auto' : 'smooth' });
	return true;
};
